import { type MessageDescriptor } from 'react-intl-next';

import { ResourceType } from '@atlassian/search-client';

import { NounSubtypeKey } from '../../constants/filters/universal-type';
import { ProductKeys } from '../../constants/products';

import { messages } from './messages';

export const getDocumentSubtypeLabel = ({
	product,
	subtype,
}: {
	product?: string;
	subtype?: string | null;
}): MessageDescriptor | undefined => {
	if (!product || !subtype) {
		return;
	}
	const SUBTYPE_DOCUMENT = 'DOCUMENT';
	const SUBTYPE_PRESENTATION = 'PRESENTATION';
	const SUBTYPE_SPREADSHEET = 'SPREADSHEET';

	type DocumentSubtypeLabels = Record<string, MessageDescriptor>;
	const GOOGLE_LABELS: DocumentSubtypeLabels = {
		[SUBTYPE_DOCUMENT]: messages.googleDocsLabel,
		[SUBTYPE_PRESENTATION]: messages.googleSlidesLabel,
		[SUBTYPE_SPREADSHEET]: messages.googleSheetsLabel,
	};

	const MICROSOFT_LABELS: DocumentSubtypeLabels = {
		[SUBTYPE_DOCUMENT]: messages.msWordLabel,
		[SUBTYPE_PRESENTATION]: messages.msPowerpointLabel,
		[SUBTYPE_SPREADSHEET]: messages.msExcelLabel,
	};

	const normalizedSubtype = subtype?.toUpperCase();

	let label;
	if (product === ProductKeys.Sharepoint || product === ProductKeys.OneDrive) {
		label = MICROSOFT_LABELS[normalizedSubtype];
	} else if (product.includes(ProductKeys.GoogleDrive)) {
		label = GOOGLE_LABELS[normalizedSubtype];
	} else if (
		product.includes(ProductKeys.AtlassianWebCrawler) &&
		// QF returns subtype with underscores, f.e. 'WEB_PAGE', so we need to support both
		subtype.replace('_', '-') === NounSubtypeKey.WebPage
	) {
		label = messages.webPageLabel;
	}

	return label;
};

export const getSubtypeLabel = ({
	product,
	subtype,
	resourceType,
}: {
	product?: string;
	subtype?: string | null;
	resourceType?: string | null;
}): MessageDescriptor | undefined => {
	const documentLabel = getDocumentSubtypeLabel({ product, subtype });
	if (documentLabel) {
		return documentLabel;
	}

	if (!resourceType) {
		return;
	}

	if (resourceType === ResourceType.VIDEO) {
		return messages.videoLabel;
	} else if (resourceType === ResourceType.REPOSITORY) {
		return messages.repositoryLabel;
	} else if (resourceType === ResourceType.PULL_REQUEST) {
		return messages.pullRequestLabel;
	} else if (resourceType === ResourceType.BRANCH) {
		return messages.branchLabel;
	} else if (resourceType === ResourceType.COMMIT) {
		return messages.commitLabel;
	}

	return undefined;
};

export const getRemoteLinkSubtypeLabel = ({
	product,
	subtype,
}: {
	product?: string;
	subtype?: string;
}): MessageDescriptor | undefined => {
	if (!product || !subtype) {
		return;
	}

	const labelKey = `${subtype}Label` as keyof typeof messages;
	if (labelKey in messages) {
		return messages[labelKey];
	} else {
		return messages.otherLabel;
	}
};
